import { isNumber } from 'lodash';
import { useEffect, useState } from 'react';
import { InputNumber, SelectAngle, SliderInput } from '@meitu/saas-ui';
import {
    calculateRealDistance,
    calculateVirtualDistance,
    createDefaultShadow,
    type VectorLayer,
} from '@meitu/saas-editor-core/es/layers/vector-layer';
import { transferXYToAngle, translateAngleToXY } from '@meitu/saas-editor-core/es/utils/math-kit';
import { type IVectorLayerProps } from '@meitu/saas-editor-core/es/version-types';
import { ColorGroup } from '@meitu/xdesign-widgets';
import { EffectPropsContainer } from '@/components/effect-props-container';
import { EffectPropsGroup } from '@/components/effect-props-group';
import LabelAction from '@/components/label-action';
import { useEditor } from '@/hooks/use-editor';
import { useGlobalWidget } from '@/hooks/use-global-widget';
import { useListenActivePickLayerProps } from '@/hooks/use-listen-layer-props';
import { useLocaleStore } from '@/i18n';
import { type WidgetCommonProps } from '../../types';
import { Tracker } from '../../utils';
import './style.less';

type Props = WidgetCommonProps;

const FALLBACK_COLOR = '#000000ff';
const FALLBACK_OPACITY = 30;

export const ShadowInspector: React.FC<Props> = ({ layerId, recordId }) => {
    const { $t } = useLocaleStore();

    const editor = useEditor();
    const props = useListenActivePickLayerProps(editor, (layer) => {
        if (!layer) return null;

        const { width: layerWidth } = layer;
        const layerData = layer.data as IVectorLayerProps;
        const effect = layerData.effect;

        if (!effect || !effect.shadow) return null;

        const shadowEffect = effect.shadow;
        const shadow = shadowEffect.length > 0 ? shadowEffect[0] : null;

        return {
            layerWidth,
            shadow,
        };
    });

    const popoverWidgetRef = useGlobalWidget(editor, false);

    const layerWidth = props ? props.layerWidth : 0;
    const shadow = props ? props.shadow : null;

    const hidden = shadow ? Boolean(shadow.hidden) : true;
    const color = shadow ? shadow.color : FALLBACK_COLOR;
    const opacity = shadow ? Math.round(shadow.opacity * 100) : FALLBACK_OPACITY;
    const angle = shadow ? transferXYToAngle(shadow.x, shadow.y) : 0;
    const blur = shadow ? shadow.blur : 0;
    const distance = shadow ? calculateVirtualDistance(shadow.x, shadow.y, layerWidth) : 0;

    // 由于 angle、distance 都是由 x、y 属性计算而来
    // 所以在修改某一项值的时候，都有可能影响到另外一项数据
    // 所以在组件内单独维护 angle、distance 的状态
    const [angleValue, setAngleValue] = useState(angle);
    const [distanceValue, setDistanceValue] = useState(distance);

    const add = () => {
        if (shadow) return;

        const defaultShadow = createDefaultShadow(layerWidth);
        editor.getInstance<VectorLayer>('VectorLayer').addShadow(layerId, defaultShadow);

        setAngleValue(transferXYToAngle(defaultShadow.x, defaultShadow.y));
        setDistanceValue(calculateVirtualDistance(defaultShadow.x, defaultShadow.y, layerWidth));

        Tracker.addShadow({
            recordId,
            stageType: editor.services.editor.getCanvasType(),
        });
    };

    const remove = () => {
        if (!shadow) return;
        editor.getInstance<VectorLayer>('VectorLayer').removeShadow(layerId);

        Tracker.removeShadow({
            recordId,
            stageType: editor.services.editor.getCanvasType(),
        });
    };

    const setVisibility = (flag: boolean) => {
        if (!shadow) return;
        editor.getInstance<VectorLayer>('VectorLayer').setShadowVisibility(layerId, flag);

        Tracker.updateShadowProps({
            recordId,
            stageType: editor.services.editor.getCanvasType(),
        });
    };

    const handleColorPickerOpenChange = (open: boolean) => {
        popoverWidgetRef.current = open;

        if (open) {
            Tracker.openShadowColorPicker({
                recordId,
                stageType: editor.services.editor.getCanvasType(),
            });
        }
    };

    const adjustColor = (value: string, options = { writeHistory: true }) => {
        if (!shadow) return;

        editor.getInstance<VectorLayer>('VectorLayer').adjustShadowProps(layerId, { color: value }, options);

        if (options.writeHistory) {
            Tracker.updateShadowProps({
                recordId,
                stageType: editor.services.editor.getCanvasType(),
                isColor: true,
            });
        }
    };

    // FIXME: distance 为 0 时，调整 angle 没有意义
    // 此时应该在 UI 上禁用，已和产品反馈，在下一个迭代中处理
    const adjustAngle = (value: number, options = { writeHistory: true }) => {
        if (!shadow || !isNumber(value)) return;

        if (distanceValue === 0) {
            setAngleValue(value);
            return;
        }

        const realDistance = calculateRealDistance(distanceValue, layerWidth);
        const { x, y } = translateAngleToXY(value, realDistance);

        editor.getInstance<VectorLayer>('VectorLayer').adjustShadowProps(layerId, { x, y }, options);

        if (options.writeHistory) {
            Tracker.updateShadowProps({
                recordId,
                stageType: editor.services.editor.getCanvasType(),
                isColor: true,
            });
        }
    };

    const adjustBlur = (value: number, options = { writeHistory: true }) => {
        if (!shadow || !isNumber(value)) return;

        editor.getInstance<VectorLayer>('VectorLayer').adjustShadowProps(layerId, { blur: value }, options);

        if (options.writeHistory) {
            Tracker.updateShadowProps({
                recordId,
                stageType: editor.services.editor.getCanvasType(),
                isColor: true,
            });
        }
    };

    const adjustDistance = (value: number, options = { writeHistory: true }) => {
        if (!shadow || !isNumber(value)) return;

        const newDistance = calculateRealDistance(value, layerWidth);
        const { x, y } = translateAngleToXY(angleValue, newDistance);

        editor.getInstance<VectorLayer>('VectorLayer').adjustShadowProps(layerId, { x, y }, options);

        if (options.writeHistory) {
            Tracker.updateShadowProps({
                recordId,
                stageType: editor.services.editor.getCanvasType(),
                isColor: true,
            });
        }
    };

    const adjustOpacity = (value: number, options = { writeHistory: true }) => {
        if (!shadow || !isNumber(value)) return;

        const opacity = value / 100;
        editor.getInstance<VectorLayer>('VectorLayer').adjustShadowProps(layerId, { opacity }, options);

        if (options.writeHistory) {
            Tracker.updateShadowProps({
                recordId,
                stageType: editor.services.editor.getCanvasType(),
                isColor: true,
            });
        }
    };

    // 由于 angleValue、distanceValue 是 UI 层单独维护的状态
    // 所以需要根据 angle、distance 的变化进行同步
    useEffect(() => {
        setAngleValue(angle);
    }, [angle]);

    useEffect(() => {
        setDistanceValue(distance);
        if (distance === 0 && angleValue !== 0) {
            setAngleValue(angleValue);
        }
    }, [distance]);

    return (
        <EffectPropsContainer
            icon="DropshadowBold"
            title={$t((v) => v.panel.vectorPanel.effect.shadow)}
            showAdd={shadow == null}
            visible={!hidden}
            onAdd={add}
            onRemove={remove}
            onToggleVisible={() => setVisibility(!hidden)}
        >
            {shadow ? (
                <div className="vector-shadow-inspector">
                    <EffectPropsGroup disabled={hidden}>
                        {/* 颜色、角度 */}
                        <LabelAction title={$t((v) => v.panel.vectorPanel.color)} align>
                            <div className="vector-shadow-inspector__color">
                                <ColorGroup
                                    popupTriggerSize={{ width: 58, height: 26 }}
                                    color={color}
                                    setColor={(color) => adjustColor(color, { writeHistory: false })}
                                    setColorAfter={(color, _, { isBrandColor }) => {
                                        adjustColor(color, { writeHistory: true });

                                        if (isBrandColor) {
                                            window._stat_track?.('hb_layers_func_use', {
                                                func_id: '1_107',
                                            });
                                        }
                                    }}
                                    onOpenChange={handleColorPickerOpenChange}
                                    showIcon={false}
                                    popupOverlayClassName="effect-color-picker"
                                    placement="left"
                                    onToggleBizTab={(bizTab) => {
                                        if (bizTab === 'team') {
                                            window._stat_track?.('hb_layers_func_click', {
                                                func_id: '1_107',
                                            });
                                        }
                                    }}
                                />
                                <div className="vector-shadow-inspector__angle">
                                    <SelectAngle
                                        angle={angleValue}
                                        showValue={false}
                                        onChange={(angle) => adjustAngle(angle, { writeHistory: false })}
                                        onAfterChange={(angle) => adjustAngle(angle, { writeHistory: true })}
                                    />
                                    <InputNumber
                                        value={angleValue}
                                        precision={1}
                                        min={-180}
                                        max={180}
                                        style={{
                                            width: 58,
                                            height: 26,
                                            marginLeft: 8,
                                        }}
                                        showStepBtn
                                        onChange={(angle) => {
                                            if (angle != null) adjustAngle(angle, { writeHistory: false });
                                        }}
                                        onChangeAfter={(angle) => {
                                            if (angle != null) adjustAngle(angle, { writeHistory: true });
                                        }}
                                    />
                                </div>
                            </div>
                        </LabelAction>

                        {/* 模糊 */}
                        <LabelAction title={$t((v) => v.panel.vectorPanel.blur)} align>
                            <SliderInput
                                showStepBtn
                                value={blur}
                                min={0}
                                max={100}
                                precision={1}
                                type="horizontal"
                                onChange={(value) => adjustBlur(value, { writeHistory: false })}
                                onAfterChange={(value) => adjustBlur(value, { writeHistory: true })}
                            />
                        </LabelAction>

                        {/* 距离 */}
                        <LabelAction title={$t((v) => v.panel.vectorPanel.effect.distance)} align>
                            <SliderInput
                                showStepBtn
                                value={distanceValue}
                                min={0}
                                max={100}
                                precision={1}
                                type="horizontal"
                                onChange={(value) => adjustDistance(value, { writeHistory: false })}
                                onAfterChange={(value) => adjustDistance(value, { writeHistory: true })}
                            />
                        </LabelAction>

                        {/* 透明度 */}
                        <LabelAction title={$t((v) => v.panel.vectorPanel.opacity)} align>
                            <SliderInput
                                showStepBtn
                                value={opacity}
                                min={0}
                                max={100}
                                precision={1}
                                type="horizontal"
                                onChange={(value) => adjustOpacity(value, { writeHistory: false })}
                                onAfterChange={(value) => adjustOpacity(value, { writeHistory: true })}
                            />
                        </LabelAction>
                    </EffectPropsGroup>
                </div>
            ) : null}
        </EffectPropsContainer>
    );
};
